<template>
    <div class="skills-view" @touchstart="handleTouchStart" @touchmove="handleTouchMove">
      <h1 class="main-title">Skills</h1>

      <div class="skills-container">
        <span
          v-for="(skill, sIndex) in filteredSkills"
          :key="sIndex"
          class="skill-tag"
          :style="{
            borderColor: skill.categoryColor,
            backgroundColor: skill.categoryColor + '33',
            '--current-glow-color-rgb': hexToRgb(skill.categoryColor),
            '--hover-translate-y': skill.randomTranslateY + 'px',
            '--hover-rotate': skill.randomRotate + 'deg',
            '--hover-duration': skill.randomDuration + 's',
            '--hover-delay': skill.randomDelay + 's',
          }"
          :class="{ 'highlighted-skill': (selectedCategory === null || selectedCategory === skill.category), 'darkened-skill': (selectedCategory !== null && selectedCategory !== skill.category) }"
        >
          {{ skill.name }}
        </span>
      </div>

      <div class="category-filters">
        <button
          @click="setSelectedCategory(null)"
          :class="{ 'active-filter': selectedCategory === null }"
        >
          All
        </button>
        <button
          v-for="(category, index) in skills"
          :key="index"
          @click="setSelectedCategory(category.category)"
          :class="{ 'active-filter': selectedCategory === category.category }"
          :style="{ '--primary-color': category.color, '--primary-color-rgb': hexToRgb(category.color) }"
        >
          {{ category.category }}
        </button>
      </div>
    </div>
</template>

<script>
export default {
  name: 'SkillsView',
  data() {
    return {
      selectedCategory: null, // null means all categories are selected
      isLargeScreen: false, // Track screen size for desktop effects
      // Configuration for random hover effect
      hoverConfig: {
        minTranslateY: -1, // Minimum vertical hover distance in pixels
        maxTranslateY: -5, // Maximum vertical hover distance in pixels
        minRotate: -3,    // Minimum rotation in degrees
        maxRotate: 3,     // Maximum rotation in degrees
        minDuration: 5,   // Minimum animation duration in seconds
        maxDuration: 10,   // Maximum animation duration in seconds
        minDelay: 0,      // Minimum animation delay in seconds
        maxDelay: 1,      // Maximum animation delay in seconds
      },
      skills: [
        {
          category: 'Frontend',
          icon: '💻',
          color: '#4285F4', // Blue
          items: ['HTML', 'CSS', 'JavaScript', 'Vue.js', 'Cypress', 'Flutter']
        },
        {
          category: 'Backend',
          icon: '⚙️',
          color: '#DB4437', // Red
          items: ['Python', 'Java', 'R', 'SQL (PostgreSQL, MySQL)', 'Docker']
        },
        {
          category: 'Operating Systems',
          icon: '🖥️',
          color: '#F4B400', // Yellow
          items: ['macOS', 'Linux', 'Windows']
        },
        {
          category: 'Networking & Infrastructure',
          icon: '🌐',
          color: '#0F9D58', // Green
          items: ['CCNA 1-3', 'AWS', 'Azure', "Hostinger" ]
        },
        {
          category: 'Tools & Platforms',
          icon: '🎨',
          color: '#8E44AD', // Purple
          items: ['Git','Bitbucket', 'Jira', 'VSCode', 'Figma', 'Photoshop', 'Illustrator', 'Premiere Pro', 'CorelDRAW']
        }
      ]
    };
  },
  computed: {
    allSkills() {
      // Flatten the skills array and add category information to each skill
      const flattened = this.skills.flatMap(category =>
        category.items.map(item => ({
          name: item,
          category: category.category,
          categoryColor: category.color
        }))
      );
      // Shuffle the array to randomize the order
      return this.shuffleArray(flattened.map(skill => ({
        ...skill,
        randomTranslateY: this.getRandomValue(this.hoverConfig.minTranslateY, this.hoverConfig.maxTranslateY),
        randomRotate: this.getRandomValue(this.hoverConfig.minRotate, this.hoverConfig.maxRotate),
        randomDuration: this.getRandomValue(this.hoverConfig.minDuration, this.hoverConfig.maxDuration),
        randomDelay: this.getRandomValue(this.hoverConfig.minDelay, this.hoverConfig.maxDelay),
      })));
    },
    filteredSkills() {
      // filteredSkills should always return allSkills now, as the filtering is done via CSS classes
      return this.allSkills;
    }
  },
  mounted() {
    this.updateIsLargeScreen();
  },
  methods: {
    setSelectedCategory(category) {
      this.selectedCategory = category;
    },
    shuffleArray(array) {
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
      return array;
    },
    hexToRgb(hex) {
      if (!hex) return "0,0,0"; // Default black if hex is undefined
      let r = 0, g = 0, b = 0;
      // Handle #RRGGBB or #RGB
      if (hex.length === 7) {
        r = parseInt(hex.substring(1, 3), 16);
        g = parseInt(hex.substring(3, 5), 16);
        b = parseInt(hex.substring(5, 7), 16);
      } else if (hex.length === 4) {
        r = parseInt(hex[1] + hex[1], 16);
        g = parseInt(hex[2] + hex[2], 16);
        b = parseInt(hex[3] + hex[3], 16);
      }
      return `${r}, ${g}, ${b}`;
    },
    handleTouchStart(event) {
      this.startY = event.touches[0].clientY;
    },
    handleTouchMove(event) {
      const touchY = event.touches[0].clientY;
      const deltaY = touchY - this.startY;

      const scrollTop = this.$el.scrollTop;
      const scrollHeight = this.$el.scrollHeight;
      const offsetHeight = this.$el.offsetHeight;

      // Prevent overscrolling
      if ((scrollTop === 0 && deltaY > 0) || (scrollTop + offsetHeight >= scrollHeight && deltaY < 0)) {
        event.preventDefault();
      }
    },
    updateIsLargeScreen() {
      this.isLargeScreen = window.innerWidth >= 1024;
    },
    getRandomValue(min, max) {
      return Math.random() * (max - min) + min;
    }
  }
}
</script>

<style scoped>
.skills-view {
  color: var(--text-color); /* Use dynamic text color */
  min-height: 100vh; /* Ensure it takes full height */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center; /* Vertically center content */
  gap: 40px; /* Add gap between sections */
  padding: 40px 20px; /* Add some padding */
  text-align: center;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  overflow-y: auto; /* Allow vertical scrolling */
}

.skills-view h2 {
  font-size: 1.5rem;
  margin-bottom: 0; /* Remove specific margin, spacing handled by parent gap */
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.skills-container {
  display: flex;
  flex-wrap: wrap; /* Allow skills to wrap */
  justify-content: center;
  gap: 15px; /* Gap between skill tags */
  max-width: 1200px;
  width: 100%;
  padding: 20px; /* Padding inside the container */
  background-color: transparent; /* Use frosted background for the overall container */
  backdrop-filter: blur(10px); /* Apply blur effect */
  border-radius: 20px;
  box-shadow: none; /* Removed box-shadow */
  border: none; /* Removed border */
  position: relative; /* Needed for absolute positioning of skill tags */
}

.skill-tag {
  background-color: var(--tag-bg-color); /* This will be overridden by inline style */
  color: var(--tag-text-color);
  padding: 12px 25px; /* Larger padding for a more prominent tag */
  border-radius: 25px; /* More rounded corners */
  font-size: 1.1rem; /* Larger font size */
  font-weight: 600; /* Bolder text */
  white-space: nowrap;
  transition: transform 0.2s ease, background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease, opacity 0.3s ease, filter 0.3s ease;
  border: 2px solid transparent; /* Default transparent border */
  cursor: default; /* Not clickable like buttons */
  animation: hover-float var(--hover-duration) ease-in-out var(--hover-delay) infinite; /* Apply the floating animation with dynamic duration and delay */
}

/* Remove variations for animation delays and durations as they are now handled by inline styles */
/* .skill-tag:nth-of-type(3n+1) { */
/*   animation-duration: 4.5s; */
/*   animation-delay: 0.5s; */
/* } */

/* .skill-tag:nth-of-type(3n+2) { */
/*   animation-duration: 3.8s; */
/*   animation-delay: 0.2s; */
/* } */

/* .skill-tag:nth-of-type(3n) { */
/*   animation-duration: 4.2s; */
/*   animation-delay: 0.8s; */
/* } */

.skill-tag:hover {
  transform: translateY(-5px) scale(1.08); /* More pronounced hover effect */
  box-shadow: 0 5px 20px rgba(var(--current-glow-color-rgb), 0.3); /* Glow on hover */
}

/* Highlighted and Darkened styles */
.highlighted-skill {
  /* Styles for selected skills, will inherit color via inline style */
  box-shadow: 0 0 20px rgba(var(--current-glow-color-rgb), 0.5); /* Stronger glow effect for highlighted */
  transform: scale(1.05); /* Slightly larger when highlighted */
}

.main-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 12px;
  background: linear-gradient(135deg, var(--text-color) 0%, #F5CD17FF 50%, #98F321FF 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.darkened-skill {
  opacity: 0.2; /* Significantly darken non-selected skills */
  filter: grayscale(100%) brightness(50%); /* Make them grayscale and darker */
  transform: scale(0.95); /* Slightly smaller when darkened */
}

/* Category Filters */
.category-filters {
  margin-top: 0; /* Remove specific margin, spacing handled by parent gap */
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 15px;
}

.category-filters button {
  background-color: var(--frosted-bg); /* Use frosted background for buttons */
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: var(--text-color);
  padding: 10px 20px;
  border-radius: 25px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  transition: background-color 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease, color 0.3s ease, border-color 0.3s ease;
}

.category-filters button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.category-filters button.active-filter {
  background-color: var(--primary-color); /* A more prominent color for active filter, now dynamic */
  color: var(--text-color); /* Use dynamic text color for active filter */
  box-shadow: 0 0 20px rgba(var(--primary-color-rgb), 0.7); /* Glow for active filter, now dynamic */
  transform: scale(1.05);
  border-color: var(--primary-color); /* Add border color to active filter */
}

/* Media Queries for responsiveness */
@media (max-width: 768px) {
  .skills-view {
    gap: 30px; /* Adjust gap for smaller screens */
  }
  .skills-view h2 {
    font-size: 2rem;
    margin-bottom: 0;
  }

  .skills-container {
    gap: 10px;
    padding: 15px;
  }

  .skill-tag {
    padding: 8px 18px; /* Slightly smaller padding */
    font-size: 0.9rem; /* Slightly smaller font size */
    border-radius: 20px;
  }

  .category-filters {
    margin-top: 0;
    gap: 10px;
  }

  .category-filters button {
    padding: 8px 15px;
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
  .skills-view {
    gap: 20px; /* Adjust gap for even smaller screens */
  }
  .skills-view h2 {
    font-size: 1.3rem;
    margin-bottom: 0;
  }

  .skills-container {
    gap: 8px; /* Even smaller gap */
    padding: 10px; /* Even smaller padding */
  }

  .skill-tag {
    padding: 6px 14px; /* Significantly smaller padding */
    font-size: 0.8rem; /* Significantly smaller font size */
    border-radius: 18px;
  }

  .category-filters {
    margin-top: 0;
    gap: 8px;
  }

  .category-filters button {
    padding: 6px 12px;
    font-size: 0.85rem;
  }
}

/* Define new CSS variables for tags and frosted glass in :root and .dark-mode */
:root {
  --tag-bg-color: #E0E0E0; /* Default background for skill tags (will be overridden by inline style) */
  --tag-text-color: #333333; /* Default text color for skill tags */
  --frosted-bg: rgba(255, 255, 255, 0.1); /* Lighter frosted background */
  /* --primary-color and --primary-color-rgb are now set dynamically per button */
  --current-glow-color-rgb: 0, 0, 0; /* Placeholder, will be set dynamically via JS for skill-tag glow */
  --hover-translate-y: -10px; /* Default hover translation */
  --hover-rotate: 3deg; /* Default hover rotation */
}

.dark-mode {
  --tag-bg-color: #333333; /* Dark mode background for skill tags (will be overridden by inline style) */
  --tag-text-color: #FFFFFF; /* Dark mode text color for skill tags */
  --frosted-bg: rgba(0, 0, 0, 0.2); /* Darker frosted background */
  /* --primary-color and --primary-color-rgb are now set dynamically per button */
}

/* Define the floating animation */
@keyframes hover-float {
  0% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(var(--hover-translate-y)) rotate(var(--hover-rotate)); }
  100% { transform: translateY(0) rotate(0deg); }
}
</style>